import { Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { LayoutService } from '@app/core/services/layout.service';
import { ModalService } from '@app/core/services/modal.service';
import { TranslateService } from '@app/core/services/translate.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { P2DataTaskGet, P2DataTaskService } from '@app/services/p2-data-task.service';

@Component({
    selector: 'tt-p2-data-task-edit',
    templateUrl: './p2-data-task-edit.component.html',
    styleUrls: ['./p2-data-task-edit.component.css'],
    standalone: false,
})
export class P2DataTaskEditComponent implements OnInit, OnChanges {
    @Input() public p2DataTaskKeyno?: string;
    @Input() public inline = false;
    @Output() public onSave = new EventEmitter<void>();

    public page?: P2DataTaskGet;
    public lockedSave = false;
    public readonly tabletSize: MediaQueryList;

    private navigator?: TTViewNavigator;
    private translations = { error: 'error', ok: 'ok' };

    constructor(
        private dataTask: P2DataTaskService,
        private modal: ModalService,
        private translate: TranslateService,
        layout: LayoutService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) navigator?: TTViewNavigator,
    ) {
        this.tabletSize = layout.getMediaQueries().tablet;
        this.navigator = useNavigatorFor(navigator, this);
    }

    public async saveChanges(): Promise<void> {
        if (!this.page || this.lockedSave) return;

        this.lockedSave = true;
        try {
            const response = await this.dataTask.saveDataTask(this.page);
            if (response.iserror !== '0') {
                await this.modal.show({
                    type: 'warning',
                    title: this.translations.error,
                    message: response.message,
                    buttons: [{ label: this.translations.ok, cssClass: 'btn-warning', close: true }],
                });
                return;
            }

            if (this.tabletSize.matches && this.inline) {
                this.onSave.emit();
            } else {
                await this.navigator?.back();
            }
        } catch {
            // Keep the editor open and unlock saving so the user can retry.
        } finally {
            this.lockedSave = false;
        }
    }

    public ngOnInit(): void {
        void this.loadTranslations();
        if (!this.p2DataTaskKeyno) {
            const routeKeyno = this.navigator?.params['p2_datatask_keyno'];
            if (routeKeyno !== undefined && routeKeyno !== null) {
                this.p2DataTaskKeyno = `${routeKeyno}`;
                void this.loadPage(this.p2DataTaskKeyno);
            }
        }
    }

    public ngOnChanges(changes: SimpleChanges): void {
        const keyno = changes['p2DataTaskKeyno']?.currentValue;
        if (keyno !== undefined && keyno !== null && keyno !== '') {
            this.p2DataTaskKeyno = `${keyno}`;
            void this.loadPage(this.p2DataTaskKeyno);
        }
    }

    private async loadPage(keyno: string): Promise<void> {
        this.page = await this.dataTask.getDataTask({ p2_datatask_keyno: keyno });
    }

    private async loadTranslations(): Promise<void> {
        try {
            const translations = await this.translate.translateBatch(['error', 'ok'] as const);
            this.translations = { error: translations.error, ok: translations.ok };
        } catch {
            // Keep the translation IDs as fallback labels if translation loading fails.
        }
    }
}
